import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Key, Settings, LogOut } from 'lucide-react';

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      {/* プロフィールアイコン */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            className="w-9 h-9 rounded-full object-cover"
          />
        ) : (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            {user.name.charAt(0).toUpperCase()}
          </div>
        )}
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg border py-2 z-50 animate-slideIn"
          style={{
            backgroundColor: 'var(--background-card)',
            borderColor: 'var(--border-color)',
          }}
        >
          {/* ユーザー情報 */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
              {user.name}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {user.email}
            </p>
            <span
              className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full"
              style={{
                backgroundColor: 'var(--light-gray-secondary)',
                color: 'var(--text-secondary)',
              }}
            >
              {user.role === 'admin' ? '管理者' : user.role === 'user' ? 'ユーザー' : '閲覧者'}
            </span>
          </div>

          {/* メニュー項目 */}
          <Link
            to="/profile"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            style={{ color: 'var(--text-primary)' }}
          >
            <User size={18} style={{ color: 'var(--text-secondary)' }} />
            プロフィール変更
          </Link>
          <Link
            to="/change-password"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            style={{ color: 'var(--text-primary)' }}
          >
            <Key size={18} style={{ color: 'var(--text-secondary)' }} />
            パスワード変更
          </Link>
          <Link
            to="/settings"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            style={{ color: 'var(--text-primary)' }}
          >
            <Settings size={18} style={{ color: 'var(--text-secondary)' }} />
            設定
          </Link>

          <div className="border-t my-2" style={{ borderColor: 'var(--border-color)' }}></div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-red-600"
          >
            <LogOut size={18} />
            ログアウト
          </button>
        </div>
      )}
    </div>
  );
}

